'use client';

import Link from 'next/link';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';
import { ChangeEvent, FormEvent } from 'react';

interface BlogFiltersProps {
  availableYears: number[];
}

export default function BlogFilters({ availableYears }: BlogFiltersProps) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const currentQuery = searchParams.get('query') || '';
  const currentYear = searchParams.get('year') || '';

  // Function to update URL params
  const updateUrlParams = (params: { [key: string]: string | null }) => {
    const newParams = new URLSearchParams(searchParams.toString());
    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === '') {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });
    // Always reset page to 1 when filters change
    newParams.delete('page');
    router.push(`${pathname}?${newParams.toString()}`);
  };

  // Handle search form submission
  const handleSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const newQuery = formData.get('query') as string;
    updateUrlParams({ query: newQuery });
  };

  // Handle year select change
  const handleYearChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const newYear = event.target.value;
    updateUrlParams({ year: newYear });
  };

  return (
    <div className="mb-8 flex flex-col sm:flex-row gap-4 items-center justify-between w-full max-w-3xl mx-auto">
      {/* Search Form */}
      <form
        onSubmit={handleSearchSubmit}
        className="flex-grow flex flex-col sm:flex-row gap-4 w-full sm:w-auto"
      >
        {/* Hidden year input not strictly needed with client-side nav, but harmless */}
        <input type="hidden" name="year" value={currentYear} />
        <input
          type="text"
          name="query"
          placeholder="Search posts..."
          defaultValue={currentQuery}
          className="p-2 rounded bg-gray-700 border border-gray-600 placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-[#59b9c1] w-full"
        />
        <button
          type="submit"
          className="p-2 px-4 rounded bg-[#59b9c1] hover:bg-[#4aa7ae] text-white transition-colors font-medium whitespace-nowrap"
        >
          Search
        </button>
      </form>

      {/* Year Filter Dropdown */}
      {availableYears.length > 0 && (
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <label htmlFor="year-filter" className="text-gray-400 text-sm whitespace-nowrap">
            Filter by Year:
          </label>
          <select
            id="year-filter"
            value={currentYear}
            onChange={handleYearChange} // This is now allowed in a Client Component
            className="p-2 rounded bg-gray-700 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-[#59b9c1] w-full sm:w-auto"
          >
            <option value="">All Years</option>
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Clear Button - links back to base blog path */}
      <Link
        href="/blog"
        className="p-2 px-4 rounded bg-gray-700 hover:bg-gray-600 text-white text-center transition-colors font-medium whitespace-nowrap"
      >
        Clear All
      </Link>
    </div>
  );
}
